/**
 * DataMappingModal Component
 * 
 * Provides UI for mapping external organizations (Zendesk/Jira) to Edyt customers.
 * This enables automatic linking of support tickets/issues to the correct customer for AI analysis.
 * 
 * Features:
 * - Display all unmapped organizations from Zendesk/Jira
 * - Dropdown selector to choose target customer
 * - Real-time mapping creation with backend API
 * - Search and filter capabilities
 * - Mapping status indicators
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/apiClient';
// Replacing Dialog with a lightweight overlay using Card to avoid missing dialog component
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from './ui/select';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from './ui/table';
import { Badge } from './ui/badge';
import { Loader2, Search, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
// Note: useToast not available in this project; using window.alert instead

interface DataMappingModalProps {
  integrationType: 'zendesk' | 'jira' | 'trello' | 'linear' | 'monday';
  onClose: () => void;
}

interface Organization {
  id: string;
  name: string;
  details?: string;
  domain_names?: string[];
  created_at?: string;
  updated_at?: string;
  status?: 'mapped' | 'unmapped';
  mapped_customer_id?: string;
  mapped_customer_name?: string;
  potential_match?: Customer;
}

interface Customer {
  customer_id: string;
  name: string;
  domain?: string;
  domains?: string[];
}

const DataMappingModal: React.FC<DataMappingModalProps> = ({ integrationType, onClose }) => {
  const queryClient = useQueryClient();
  const notify = (title: string, description?: string, variant?: 'destructive' | 'default') => {
    const prefix = variant === 'destructive' ? '[Error] ' : '';
    window.alert(prefix + title + (description ? `\n${description}` : ''));
  };
  
  // Local state
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'unmapped' | 'mapped'>('unmapped');

  // Fetch organizations from integration
  const { data: organizationsData, isLoading: isLoadingOrgs, error: orgsError } = useQuery({
    queryKey: [`${integrationType}Organizations`],
    queryFn: async () => {
      if (integrationType === 'zendesk') {
        return await apiClient.get('/api/integrations/zendesk/organizations');
      } else if (integrationType === 'trello') {
        return await apiClient.get('/api/integrations/trello/organizations');
      } else if (integrationType === 'linear') {
        return await apiClient.get('/api/integrations/linear/organizations');
      } else if (integrationType === 'monday') {
        return await apiClient.get('/api/integrations/monday/organizations');
      } else {
        return await apiClient.get('/api/integrations/jira/organizations');
      }
    },
    retry: 1
  });

  // Fetch customers
  const { data: customersData, isLoading: isLoadingCustomers, error: customersError } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      console.log('[DEBUG] Fetching customers...');
      const result = await apiClient.getCustomers();
      console.log('[DEBUG] Customers response:', result);
      return result;
    },
    retry: 3,
    staleTime: 30000, // Cache for 30 seconds
  });

  // Mapping creation mutation
  const mappingMutation = useMutation({
    mutationFn: async ({ 
      customerId, 
      externalId, 
      externalName 
    }: { 
      customerId: string; 
      externalId: string; 
      externalName: string; 
    }) => {
      return await apiClient.post(`/api/customers/${customerId}/mappings`, {
        type: `${integrationType}_org`,
        external_id: externalId,
        external_name: externalName
      });
    },
    onSuccess: (data, variables) => {
      const issuesUpdated = (data as any)?.issues_updated || 0;
      notify(
        "Mapping Created", 
        `Successfully mapped ${variables.externalName} to customer${issuesUpdated > 0 ? `. Updated ${issuesUpdated} existing issue(s).` : '.'}`
      );
      queryClient.invalidateQueries({ queryKey: [`${integrationType}Organizations`] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      // Clear the mapping from local state to update UI
      setMappings(prev => {
        const newMappings = { ...prev };
        delete newMappings[variables.externalId];
        return newMappings;
      });
      // Refetch organizations to update status
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: [`${integrationType}Organizations`] });
      }, 500);
    },
    onError: (error: any) => {
      notify("Mapping Failed", error?.message || "Failed to create mapping. Please try again.", 'destructive');
    }
  });

  // Extract organizations and customers from API responses
  const organizations: Organization[] = useMemo(() => {
    if (!organizationsData) return [];
    const payload = (organizationsData as any)?.data ?? organizationsData;
    if (integrationType === 'zendesk') {
      return payload.organizations || [];
    } else {
      // Jira, Trello, and Linear return the same shape (id/name/details/status/mapping fields)
      const organizations = payload.organizations || [];
      return organizations.map((org: any) => ({
        id: org.id,
        name: org.name,
        details: org.details,
        status: org.status,
        mapped_customer_id: org.mapped_customer_id,
        mapped_customer_name: org.mapped_customer_name,
        potential_match: org.potential_match
      }));
    }
  }, [organizationsData, integrationType]);

  const customers: Customer[] = useMemo(() => {
    console.log('[DEBUG] Processing customers data:', { customersData, organizationsData, integrationType });

    // First try to get customers from the dedicated customers query
    if (customersData) {
      const payload = (customersData as any)?.data ?? customersData;
      const customersList = payload?.customers || [];
      console.log('[DEBUG] Customers from customers query:', customersList.length, customersList);
      if (customersList.length > 0) {
        return customersList;
      }
    }

    // Fallback to getting customers from organizations endpoint (for Jira / Trello / Linear / Monday)
    if ((integrationType === 'jira' || integrationType === 'trello' || integrationType === 'linear' || integrationType === 'monday') && organizationsData) {
      const payload = (organizationsData as any)?.data ?? organizationsData;
      const customersList = payload?.customers || [];
      console.log('[DEBUG] Customers from organizations query:', customersList.length, customersList);
      if (customersList.length > 0) {
        return customersList;
      }
    }

    console.log('[DEBUG] No customers found');
    // Final fallback - return empty array
    return [];
  }, [customersData, organizationsData, integrationType]);

  // Filter and search organizations
  const filteredOrganizations = useMemo(() => {
    return organizations.filter(org => {
      // Search filter
      if (searchQuery) {
        const matchesSearch = org.name.toLowerCase().includes(searchQuery.toLowerCase());
        if (!matchesSearch) return false;
      }

      // Status filter
      if (filterStatus !== 'all') {
        if (filterStatus === 'mapped' && org.status !== 'mapped') return false;
        if (filterStatus === 'unmapped' && org.status !== 'unmapped') return false;
      }

      return true;
    });
  }, [organizations, searchQuery, filterStatus]);

  // Handlers
  const handleMappingChange = (orgId: string, customerId: string) => {
    setMappings(prev => ({ ...prev, [orgId]: customerId }));
  };

  const handleSave = (orgId: string, orgName: string) => {
    const customerId = mappings[orgId];
    if (!customerId) {
      notify("No Customer Selected", "Please select a customer before saving.", 'destructive');
      return;
    }
    
    mappingMutation.mutate({
      customerId,
      externalId: orgId,
      externalName: orgName
    });
  };

  const handleSaveAll = () => {
    const mappingCount = Object.keys(mappings).length;
    if (mappingCount === 0) {
      notify("No Mappings Selected", "Please select customers for organizations before saving.", 'destructive');
      return;
    }
    
    // Save all mappings
    Object.entries(mappings).forEach(([orgId, customerId]) => {
      const org = organizations.find(o => o.id === orgId);
      if (org) {
        mappingMutation.mutate({
          customerId,
          externalId: orgId,
          externalName: org.name
        });
      }
    });
  };

  // Loading state
  if (isLoadingOrgs || isLoadingCustomers) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
        <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
          <Card>
            <CardContent className="p-8">
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-3 text-lg">
                  {isLoadingOrgs ? 'Loading organizations...' : 'Loading customers...'}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Error state
  if (orgsError) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
        <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-red-600">
                <XCircle className="h-5 w-5 mr-2" />
                Error Loading Organizations
              </CardTitle>
              <CardDescription>
                Failed to load organizations from {integrationType}. Please ensure your integration is connected and try again.
              </CardDescription>
            </CardHeader>
            <CardContent className="pb-6">
              <Button onClick={onClose}>Close</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const integrationName = integrationType.charAt(0).toUpperCase() + integrationType.slice(1);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-bold">Map {integrationName} Organizations to Accounts</CardTitle>
            <CardDescription className="text-base">Link organizations from {integrationName} to your account profiles in Edyt for automated ticket analysis and insights.</CardDescription>
          </CardHeader>
          <CardContent>

        {/* Search and Filter Controls */}
        <div className="flex items-center gap-4 py-4 border-b">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search organizations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Select value={filterStatus} onValueChange={(value: any) => setFilterStatus(value)}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="unmapped">Unmapped</SelectItem>
              <SelectItem value="mapped">Mapped</SelectItem>
            </SelectContent>
          </Select>

          <Button 
            onClick={handleSaveAll} 
            disabled={Object.keys(mappings).length === 0 || mappingMutation.isPending}
            className="whitespace-nowrap"
          >
            {mappingMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save All ({Object.keys(mappings).length})
          </Button>
        </div>

        {/* Statistics */}
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <Badge variant="secondary">
            {filteredOrganizations.length} organizations
          </Badge>
          <Badge variant="secondary">
            {customers.length} accounts available
          </Badge>
          {Object.keys(mappings).length > 0 && (
            <Badge variant="default">
              {Object.keys(mappings).length} pending mappings
            </Badge>
          )}
          {customersError && (
            <Badge variant="destructive">
              Customers Error
            </Badge>
          )}
        </div>

        {/* Organizations Table */}
        <div className="max-h-[50vh] overflow-y-auto border rounded-lg">
          {filteredOrganizations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No Organizations Found</h3>
              <p className="text-sm text-gray-500 mt-2 max-w-md">
                {searchQuery
                  ? "No organizations match your search. Try a different query."
                  : `No organizations found in ${integrationName}. Import data first or adjust your filter.`}
              </p>
            </div>
          ) : customers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No Accounts Available</h3>
              <p className="text-sm text-gray-500 mt-2 max-w-md">
                You need to create accounts first before you can map organizations to them.
                Go to the Accounts page to add accounts.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[30%]">{integrationName} Organization</TableHead>
                  <TableHead className="w-[40%]">Map to Edyt Account</TableHead>
                  <TableHead className="w-[15%] text-center">Status</TableHead>
                  <TableHead className="w-[15%] text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrganizations.map((org) => (
                  <TableRow key={org.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{org.name}</div>
                        {org.details && (
                          <div className="text-sm text-gray-500">{org.details}</div>
                        )}
                        {org.domain_names && org.domain_names.length > 0 && (
                          <div className="text-xs text-gray-400 mt-1">
                            Domains: {org.domain_names.join(', ')}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {customers.length > 0 ? (
                        <Select
                          value={mappings[org.id] || ''}
                          onValueChange={(value) => handleMappingChange(org.id, value)}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select a customer..." />
                          </SelectTrigger>
                          <SelectContent>
                            {customers.map((customer) => (
                              <SelectItem key={customer.customer_id} value={customer.customer_id}>
                                <div className="flex flex-col">
                                  <span>{customer.name}</span>
                                  {customer.domain && (
                                    <span className="text-xs text-gray-500">{customer.domain}</span>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="text-sm text-gray-500 italic">
                          No accounts available - create accounts first
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {org.status === 'mapped' ? (
                        <Badge variant="default" className="whitespace-nowrap bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Mapped to account {org.mapped_customer_name}
                        </Badge>
                      ) : mappings[org.id] ? (
                        <Badge variant="default" className="whitespace-nowrap">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Ready
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="whitespace-nowrap">
                          Unmapped
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        onClick={() => handleSave(org.id, org.name)}
                        disabled={org.status === 'mapped' || !mappings[org.id] || mappingMutation.isPending || customers.length === 0}
                        variant="outline"
                      >
                        {mappingMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          'Save'
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-gray-600">
            Tip: Map organizations to enable automatic ticket-to-customer linking in AI insights.
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DataMappingModal;

